// webcrypto-support.service.ts
import { Injectable, InjectionToken, inject } from '@angular/core';
import {
  Observable, defer, from, of, Subject,
  switchMap, catchError, tap, shareReplay
} from 'rxjs';

export interface AlgoSupport {
  name: string;
  supported: boolean;
  detail?: string;
}

export interface WebCryptoReport {
  ts: string;
  secureContext: boolean;
  platform: {
    userAgent: string;
    platform: string;
    language: string;
  };
  webcrypto: AlgoSupport[];
}

export interface WebCryptoSupportConfig {
  /** Endpoint to POST the report to (e.g., '/api/crypto-support'). */
  endpoint: string;
  /** Cache TTL in milliseconds (default: 30 days). */
  cacheTtlMs?: number;
  /** LocalStorage key to cache the last successful payload. */
  cacheKey?: string;
  /** Skip network POST; only detect & cache. */
  dryRun?: boolean;
}

export const WEBCRYPTO_SUPPORT_CONFIG = new InjectionToken<WebCryptoSupportConfig>('WEBCRYPTO_SUPPORT_CONFIG');

@Injectable({ providedIn: 'root' })
export class WebCryptoSupportService {
  private cfg = inject(WEBCRYPTO_SUPPORT_CONFIG, { optional: true }) ?? {
    endpoint: '/api/crypto-support',
    cacheTtlMs: 30 * 24 * 60 * 60 * 1000,
    cacheKey: 'webcrypto_support_cache_v1',
    dryRun: false,
  } as WebCryptoSupportConfig;

  /** Imperative refresh trigger (used by refresh$()) */
  private refreshTrigger$ = new Subject<void>();

  /**
   * Lazily ensures there's a valid (TTL) report:
   * - If a valid cache exists, emits o cache.
   * - Caso contrário, executa os probes, cacheia e (opcionalmente) POSTa.
   * Hot + shared para evitar chamadas concorrentes duplicadas.
   */
  private ensureReportedShared$ = defer(() => this.loadOrProbe$()).pipe(
    // Keep the latest emission for late subscribers; no window time (TTL handled manually)
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /** Public API: ensure reported (cached or freshly probed). */
  ensureReported$(): Observable<WebCryptoReport | null> {
    return this.ensureReportedShared$;
  }

  /** Public API: force detection & POST, ignoring cache (but updating it). */
  refresh$(): Observable<WebCryptoReport | null> {
    // trigger refresh pipeline
    this.refreshTrigger$.next();
    return this.refreshPipeline$();
  }

  /** Synchronous helper: returns cached if valid, else null. */
  getCachedSync(): WebCryptoReport | null {
    return this.readCache();
  }

  // ---------------------- Internal Rx helpers ------------------------------

  /** When refresh is requested, run probe+post+cache; shared to dedupe subscribers. */
  private refreshShared$ = this.refreshTrigger$.pipe(
    switchMap(() => this.probePostCache$()),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private refreshPipeline$(): Observable<WebCryptoReport | null> {
    // In case someone subscribes after next(), ensure at least one emission
    return this.refreshShared$.pipe(
      catchError(() => of(null))
    );
  }

  /** Decides between returning cache or probing anew. */
  private loadOrProbe$(): Observable<WebCryptoReport | null> {
    return defer(() => {
      const cached = this.readCache();
      if (cached) return of(cached);
      return this.probePostCache$();
    }).pipe(
      catchError(() => of(null))
    );
  }

  /** Probes support, builds payload, POSTs (optional) and caches. */
  private probePostCache$(): Observable<WebCryptoReport> {
    return from(this.buildReport()).pipe(
      tap((payload) => this.writeCache(payload)),
      switchMap((payload) =>
        this.cfg.dryRun ? of(payload) : this.safePost$(payload).pipe(
          catchError(() => of(void 0)),
          // map back to payload regardless of post result
          switchMap(() => of(payload))
        )
      )
    );
  }

  private safePost$(payload: WebCryptoReport): Observable<void> {
    return defer(() =>
      fetch(this.cfg.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify(payload),
      })
    ).pipe(
      // Map any response to void; errors are handled by caller
      switchMap(() => of(void 0))
    );
  }

  // ---------------------- Cache utils --------------------------------------

  private readCache(): WebCryptoReport | null {
    const key = this.cfg.cacheKey ?? 'webcrypto_support_cache_v1';
    const ttl = this.cfg.cacheTtlMs ?? 30 * 24 * 60 * 60 * 1000;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const { ts, payload } = JSON.parse(raw) as { ts: number; payload: WebCryptoReport };
      if (Date.now() - ts > ttl) {
        localStorage.removeItem(key);
        return null;
      }
      return payload;
    } catch {
      return null;
    }
  }

  private writeCache(payload: WebCryptoReport): void {
    const key = this.cfg.cacheKey ?? 'webcrypto_support_cache_v1';
    try {
      localStorage.setItem(key, JSON.stringify({ ts: Date.now(), payload }));
    } catch {
      // ignore quota errors
    }
  }

  // ---------------------- Report builders & probes -------------------------

  private isSecure(): boolean {
    return typeof window !== 'undefined' && (window as any).isSecureContext === true;
  }

  private async buildReport(): Promise<WebCryptoReport> {
    if (typeof window === 'undefined' || typeof crypto === 'undefined' || !('subtle' in crypto) || !this.isSecure()) {
      return {
        ts: new Date().toISOString(),
        secureContext: this.isSecure(),
        platform: {
          userAgent: (navigator as any)?.userAgent ?? 'unknown',
          platform: (navigator as any)?.platform ?? 'unknown',
          language: (navigator as any)?.language ?? 'unknown',
        },
        webcrypto: [{ name: 'WebCrypto (crypto.subtle over HTTPS)', supported: false, detail: 'Not available or insecure context' }],
      };
    }

    const support = await this.detectWebCryptoSupport();
    const ua =
      (navigator as any).userAgentData?.brands?.map((b: any) => `${b.brand}/${b.version}`).join(', ') ||
      navigator.userAgent;

    return {
      ts: new Date().toISOString(),
      secureContext: this.isSecure(),
      platform: {
        userAgent: ua,
        platform: navigator.platform,
        language: navigator.language,
      },
      webcrypto: support,
    };
  }

  // ---- Probers (same logic, wrapped by Rx via from/defer upstream) --------

  private async detectWebCryptoSupport(): Promise<AlgoSupport[]> {
    const tests: Array<Promise<AlgoSupport>> = [
      this.probeDigest('SHA-256').then(supported => ({ name: 'SHA-256 (digest)', supported })),
      this.probeDigest('SHA-384').then(supported => ({ name: 'SHA-384 (digest)', supported })),
      this.probeDigest('SHA-512').then(supported => ({ name: 'SHA-512 (digest)', supported })),

      this.probeHMAC('SHA-256').then(supported => ({ name: 'HMAC-SHA256', supported })),
      this.probeHMAC('SHA-384').then(supported => ({ name: 'HMAC-SHA384', supported })),
      this.probeHMAC('SHA-512').then(supported => ({ name: 'HMAC-SHA512', supported })),

      this.probeAES('AES-GCM').then(supported => ({ name: 'AES-GCM-256', supported })),
      this.probeAES('AES-CTR').then(supported => ({ name: 'AES-CTR-256', supported })),
      this.probeAES('AES-KW').then(supported => ({ name: 'AES-KW-256', supported })),

      this.probeRSA('RSA-OAEP').then(supported => ({ name: 'RSA-OAEP (SHA-256)', supported })),
      this.probeRSA('RSASSA-PKCS1-v1_5').then(supported => ({ name: 'RSASSA-PKCS1-v1_5 (SHA-256)', supported })),
      this.probeRSA('RSA-PSS').then(supported => ({ name: 'RSA-PSS (SHA-256)', supported })),

      this.probeEC('ECDSA', 'P-256').then(supported => ({ name: 'ECDSA P-256', supported })),
      this.probeEC('ECDSA', 'P-384').then(supported => ({ name: 'ECDSA P-384', supported })),
      this.probeEC('ECDSA', 'P-521').then(supported => ({ name: 'ECDSA P-521', supported })),

      this.probeEC('ECDH', 'P-256').then(supported => ({ name: 'ECDH P-256', supported })),
      this.probeEC('ECDH', 'P-384').then(supported => ({ name: 'ECDH P-384', supported })),
      this.probeEC('ECDH', 'P-521').then(supported => ({ name: 'ECDH P-521', supported })),
    ];

    return Promise.all(tests);
  }

  // ---- Low-level probes (async) ------------------------------------------

  private async probeDigest(alg: AlgorithmIdentifier): Promise<boolean> {
    try {
      const data = new TextEncoder().encode('probe');
      await crypto.subtle.digest(alg, data);
      return true;
    } catch {
      return false;
    }
  }

  private async probeHMAC(hash: 'SHA-256' | 'SHA-384' | 'SHA-512'): Promise<boolean> {
    try {
      const key = await crypto.subtle.generateKey(
        { name: 'HMAC', hash },
        false,
        ['sign', 'verify']
      );
      const sig = await crypto.subtle.sign('HMAC', key, new Uint8Array(32));
      return sig.byteLength > 0;
    } catch {
      return false;
    }
  }

  private async probeAES(name: 'AES-GCM' | 'AES-CTR' | 'AES-KW'): Promise<boolean> {
    try {
      const key = await crypto.subtle.generateKey(
        { name, length: 256 },
        false,
        name === 'AES-KW' ? ['wrapKey', 'unwrapKey'] : ['encrypt', 'decrypt']
      );
      if (name === 'AES-GCM') {
        const iv = crypto.getRandomValues(new Uint8Array(12));
        const ct = await crypto.subtle.encrypt({ name, iv }, key, new Uint8Array(16));
        return ct.byteLength > 0;
      }
      if (name === 'AES-CTR') {
        const counter = new Uint8Array(16);
        const ct = await crypto.subtle.encrypt({ name, counter, length: 64 }, key, new Uint8Array(16));
        return ct.byteLength > 0;
      }
      if (name === 'AES-KW') {
        const child = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 128 }, true, ['encrypt', 'decrypt']);
        const wrapped = await crypto.subtle.wrapKey('raw', child, key, { name: 'AES-KW' });
        return wrapped.byteLength > 0;
      }
      return false;
    } catch {
      return false;
    }
  }

  private async probeRSA(name: 'RSA-PSS' | 'RSASSA-PKCS1-v1_5' | 'RSA-OAEP'): Promise<boolean> {
    try {
      const algo: RsaHashedKeyGenParams = {
        name,
        modulusLength: 2048,
        publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
        hash: 'SHA-256',
      };
      const keyPair = await crypto.subtle.generateKey(
        algo,
        false,
        name === 'RSA-OAEP' ? ['encrypt', 'decrypt'] : ['sign', 'verify']
      );
      if (name === 'RSA-OAEP') {
        const enc = await crypto.subtle.encrypt({ name }, (keyPair as CryptoKeyPair).publicKey, new Uint8Array(8));
        return enc.byteLength > 0;
      } else {
        const sig = await crypto.subtle.sign({ name, saltLength: 32 } as any, (keyPair as CryptoKeyPair).privateKey, new Uint8Array(32));
        return sig.byteLength > 0;
      }
    } catch {
      return false;
    }
  }

  private async probeEC(name: 'ECDSA' | 'ECDH', namedCurve: 'P-256' | 'P-384' | 'P-521'): Promise<boolean> {
    try {
      const keyPair = await crypto.subtle.generateKey(
        { name, namedCurve },
        false,
        name === 'ECDSA' ? ['sign', 'verify'] : ['deriveKey', 'deriveBits']
      );
      if (name === 'ECDSA') {
        const sig = await crypto.subtle.sign({ name, hash: 'SHA-256' }, (keyPair as CryptoKeyPair).privateKey, new Uint8Array(16));
        return sig.byteLength > 0;
      } else {
        const bits = await crypto.subtle.deriveBits(
          { name, public: (keyPair as CryptoKeyPair).publicKey },
          (keyPair as CryptoKeyPair).privateKey,
          128
        );
        return bits.byteLength > 0;
      }
    } catch {
      return false;
    }
  }
}
